import { Injectable } from '@angular/core';
import { PersonalInfoInputs } from '../components/sections/client-initiated-loan/about-you/steps/borrower-personal-info/borrower-personal-info.component';
import { AboutYouSteps } from '../enums/about-you-steps.enum';
import { GettingStartedSteps } from '../enums/getting-started-steps.enum';
import { PropertySteps } from '../enums/property-steps.enum';
import { CurrentAddressInputs } from '../components/sections/client-initiated-loan/about-you/steps/borrower-current-address/borrower-current-address.component';
import { SubjectAddressInputs } from '../components/sections/client-initiated-loan/property/steps/refinance-subject-address/refinance-subject-address.component';
import { LoanPurposeOptions } from '../components/sections/client-initiated-loan/getting-started/steps/loan-purpose/loan-purpose.component';
import { StepperContextService } from './stepper-context.service';

@Injectable({
  providedIn: 'root'
})
export class StepperAnswersService {
  private stepData: { [key: string]: any } = {};

  constructor(
    public contextService: StepperContextService
  ) { }

  setAnswer(stepKey: string, answer: any): void {
    this.stepData[stepKey] = answer;
  }

  getAnswer(stepKey: string): any {
    return this.stepData[stepKey];
  }

  getAllAnswers(): { [key: string]: any } {
    return { ...this.stepData };
  }

  hasAnswer(stepKey: string): boolean {
    return this.stepData.hasOwnProperty(stepKey);
  }

  clear(): void {
    this.stepData = {};
  }

  prepopulateQueryParams(rawQueryParams) {
    const queryParams = this.normalizeQueryParams(rawQueryParams);
    const personalInfoMappedInputs: Partial<Record<string, string>> = {};
    const subjectAddressMappedInputs: Partial<Record<string, string>> = {};
    const ownedAddressMappedInputs: Partial<Record<string, string>> = {};

    if (queryParams['email']) {
      personalInfoMappedInputs[PersonalInfoInputs.Email] = queryParams['email'];
    }
    if (queryParams['firstname']) {
      personalInfoMappedInputs[PersonalInfoInputs.FirstName] = queryParams['firstname'];
    }
    if (queryParams['lastname']) {
      personalInfoMappedInputs[PersonalInfoInputs.LastName] = queryParams['lastname'];
    }

    if (Object.keys(personalInfoMappedInputs).length > 0) {
      this.setAnswer(AboutYouSteps.BorrowerPersonalInfo, personalInfoMappedInputs);
    }

    if (queryParams['purpose']) {
      let capitalized = queryParams['purpose'].charAt(0).toUpperCase() + queryParams['purpose'].slice(1);
      this.setAnswer(GettingStartedSteps.LoanPurpose, capitalized);
      this.contextService.purposePrepopulated = true
      if (this.getAnswer(GettingStartedSteps.LoanPurpose) === 'Purchase') {
        if (queryParams['subjectcity']) {
          subjectAddressMappedInputs[SubjectAddressInputs.City] = queryParams['subjectcity'];
        }
        if (queryParams['subjectstate']) {
          subjectAddressMappedInputs[SubjectAddressInputs.State] = queryParams['subjectstate'];
        }
        if (queryParams['subjectzip']) {
          subjectAddressMappedInputs[SubjectAddressInputs.Zip] = queryParams['subjectzip'];
        }

        if (Object.keys(subjectAddressMappedInputs).length > 0) {
          this.setAnswer(PropertySteps.PurchaseSubjectAddress, subjectAddressMappedInputs);
        }
      } else {
        if (queryParams['subjectstreet']) {
          subjectAddressMappedInputs[SubjectAddressInputs.Address1] = queryParams['subjectstreet'];
        }
        if (queryParams['subjectcity']) {
          subjectAddressMappedInputs[SubjectAddressInputs.City] = queryParams['subjectcity'];
        }
        if (queryParams['subjectstate']) {
          subjectAddressMappedInputs[SubjectAddressInputs.State] = queryParams['subjectstate'];
        }
        if (queryParams['subjectzip']) {
          subjectAddressMappedInputs[SubjectAddressInputs.Zip] = queryParams['subjectzip'];
        }

        if (Object.keys(subjectAddressMappedInputs).length > 0) {
          this.setAnswer(PropertySteps.RefinanceSubjectAddress, subjectAddressMappedInputs);
        }
      }
    }

    if (queryParams['usage']) {
      const input = queryParams['usage'];
      const formatted = input
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      this.setAnswer(PropertySteps.PropertyUsage, formatted);
    }

    if (queryParams['ownedstreet']) {
      ownedAddressMappedInputs[CurrentAddressInputs.Address1] = queryParams['ownedstreet'];
    }
    if (queryParams['ownedcity']) {
      ownedAddressMappedInputs[CurrentAddressInputs.City] = queryParams['ownedcity'];
    }
    if (queryParams['ownedstate']) {
      ownedAddressMappedInputs[CurrentAddressInputs.State] = queryParams['ownedstate'];
    }
    if (queryParams['ownedzip']) {
      ownedAddressMappedInputs[CurrentAddressInputs.Zip] = queryParams['ownedzip'];
    }

    if (Object.keys(ownedAddressMappedInputs).length > 0) {
      this.setAnswer(AboutYouSteps.BorrowerCurrentAddress, ownedAddressMappedInputs);
    }
  }

  normalizeQueryParams(queryParams: Record<string, string>): Record<string, string> {
    return Object.keys(queryParams).reduce((acc, key) => {
      const normalizedKey = key.toLowerCase();
      const normalizedValue = queryParams[key].replace(/_/g, ' ');
      acc[normalizedKey] = normalizedValue;
      return acc;
    }, {} as Record<string, string>);
  }

  loadClientInfo(clientInfo: any, clientAddress?: any) {
    this.contextService.clientInfo = clientInfo
    const personalInfo: Partial<Record<string, string>> = {};
    personalInfo[PersonalInfoInputs.Email] = clientInfo.email ?? ''
    personalInfo[PersonalInfoInputs.FirstName] = clientInfo.firstName ?? ''
    personalInfo[PersonalInfoInputs.LastName] = clientInfo.lastName ?? ''
    this.setAnswer(AboutYouSteps.BorrowerPersonalInfo, personalInfo);
    if (clientAddress && Object.keys(clientAddress).length > 0) {
      const currentAddress: Partial<Record<string, string>> = {};
      currentAddress[CurrentAddressInputs.Address1] = clientAddress.propertyInfo.address ?? ''
      currentAddress[CurrentAddressInputs.City] = clientAddress.propertyInfo.city ?? ''
      currentAddress[CurrentAddressInputs.State] = clientAddress.propertyInfo.state ?? ''
      currentAddress[CurrentAddressInputs.Zip] = clientAddress.propertyInfo.zip ?? ''
      this.setAnswer(AboutYouSteps.BorrowerCurrentAddress, currentAddress);
    }
  }

  loadDataFromQuickPricer(fromQuickPricer: any) {
    if (fromQuickPricer.loanPurpose) {
      this.setAnswer(GettingStartedSteps.LoanPurpose,
        fromQuickPricer.loanPurpose === LoanPurposeOptions.Purchase ? LoanPurposeOptions.Purchase : LoanPurposeOptions.Refinance);
      this.contextService.purposePrepopulated = true
    }
  }
}